/**
 * Ayarlar — KULLANICIYA ÖZEL ayarlar, yalnızca gerçekten etkisi olanlar.
 *
 * 2026-07-25 ayar denetiminden sonra bu sayfa kasten küçüktür. Öncesinde
 * burada LLM örnekleme kaydırıcıları, TTS motoru ve "varsayılan tartışma"
 * seçenekleri de vardı — hiçbiri işe yaramıyordu:
 *
 *   • `llm` / `tts` blokları: FE gönderiyordu ama backend'in
 *     `PreferencesUpdate` şeması bu alanları TANIMIYOR, Pydantic sessizce
 *     atıyordu. Canlı testle doğrulandı: veritabanına hiç yazılmıyorlardı.
 *     Bu knob'ların gerçek yeri Yönetim → Sistem Ayarları (global kapsam).
 *   • `defaults.max_rounds` / `agent_count` / `tts_enabled` /
 *     `simulation_enabled`: veritabanına yazılıyordu ama OKUYAN kimse yoktu
 *     — wizard `WIZARD_DEFAULTS` kod sabitini kullanıyor.
 *
 * Kaydedilen bir ayarın hiçbir şeyi değiştirmemesi, ayarın olmamasından
 * daha kötüdür: kullanıcı sistemi ayarladığını sanır. O yüzden silindi.
 *
 * Geriye kalan iki prompt GERÇEKTEN canlıdır — `routes_debate.py` bunları
 * her tartışma başlangıcında okuyup persona ve moderatör sistem promptuna
 * enjekte eder.
 */
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuthStore } from "../store/authStore";
import type { UserPreferences } from "../transport/authClient";
import { AppShell } from "../components/layout/AppShell";

export function SettingsPage() {
  const user = useAuthStore((s) => s.user);
  const updatePreferences = useAuthStore((s) => s.updatePreferences);
  const isAdmin = user?.role === "admin";

  const prefs: UserPreferences = user?.preferences ?? {};
  const [debatePrompt, setDebatePrompt] = useState(prefs.debate_prompt ?? "");
  const [moderatorPrompt, setModeratorPrompt] = useState(prefs.moderator_prompt ?? "");

  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user?.preferences) return;
    setDebatePrompt(user.preferences.debate_prompt ?? "");
    setModeratorPrompt(user.preferences.moderator_prompt ?? "");
  }, [user?.preferences]);

  const dirty =
    debatePrompt !== (prefs.debate_prompt ?? "") ||
    moderatorPrompt !== (prefs.moderator_prompt ?? "");

  async function handleSave() {
    setSaving(true);
    setSuccess(false);
    setError(null);
    try {
      // Boş string GÖNDERİLİR (undefined değil) — kullanıcının bir promptu
      // kasten temizlemesi ile hiç dokunmaması farklı şeylerdir.
      await updatePreferences({
        debate_prompt: debatePrompt,
        moderator_prompt: moderatorPrompt,
      });
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Kaydetme başarısız");
    } finally {
      setSaving(false);
    }
  }

  return (
    <AppShell>
      <main className="max-w-3xl mx-auto px-6 py-8 space-y-6">
        <header>
          <h1 className="text-xl font-semibold">Ayarlar</h1>
          <p className="text-sm text-slate-500 mt-1">
            Buradaki ayarlar yalnızca sizin hesabınızı etkiler. Model, ses ve
            altyapı gibi sistem genelindeki ayarlar{" "}
            {isAdmin ? (
              <Link to="/admin" className="text-indigo-400 hover:text-indigo-300 underline">
                Yönetim
              </Link>
            ) : (
              <span className="text-slate-400">Yönetim</span>
            )}{" "}
            bölümündedir.
          </p>
        </header>

        <section className="bg-white/[0.02] border border-white/5 rounded-2xl p-6">
          <h2 className="text-base font-semibold mb-4">Profil</h2>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-slate-500">İsim</span>
              <p className="mt-0.5 font-medium">{user?.display_name}</p>
            </div>
            <div>
              <span className="text-slate-500">E-posta</span>
              <p className="mt-0.5 font-medium">{user?.email}</p>
            </div>
            <div>
              <span className="text-slate-500">Şirket</span>
              <p className="mt-0.5 font-medium">{user?.company ?? "—"}</p>
            </div>
            <div>
              <span className="text-slate-500">Unvan</span>
              <p className="mt-0.5 font-medium">{user?.title ?? "—"}</p>
            </div>
          </div>
        </section>

        <section className="bg-white/[0.02] border border-white/5 rounded-2xl p-6">
          <h2 className="text-base font-semibold mb-1">Tartışma Prompt Ayarları</h2>
          <p className="text-xs text-slate-500 mb-6">
            Bu talimatlar başlattığınız tüm tartışmalara otomatik uygulanır.
          </p>

          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Genel Tartışma Talimatları
              </label>
              <p className="text-xs text-slate-500 mb-2">
                Tüm konuşmacılara uygulanacak ek kurallar, kısıtlamalar veya yönlendirmeler.
              </p>
              <textarea
                value={debatePrompt}
                onChange={(e) => setDebatePrompt(e.target.value)}
                rows={4}
                maxLength={4000}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-slate-600 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 resize-y transition-all text-sm"
                placeholder="Örn: Tartışmalarda yerlilik oranı, tedarik/ambargo riski, sertifikasyon takvimi ve bakım-idame maliyeti mutlaka değerlendirilsin."
              />
              <p className="text-xs text-slate-600 mt-1 text-right">{debatePrompt.length}/4000</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Moderatör Talimatları
              </label>
              <p className="text-xs text-slate-500 mb-2">
                Moderatörün nasıl müdahale edeceğini belirleyen özel kurallar.
              </p>
              <textarea
                value={moderatorPrompt}
                onChange={(e) => setModeratorPrompt(e.target.value)}
                rows={4}
                maxLength={4000}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-slate-600 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 resize-y transition-all text-sm"
                placeholder="Örn: Tartışma 2 tur boyunca uzlaşıya ulaşamazsa moderatör bir tedarik zinciri veya kalifikasyon uzmanı davet etsin."
              />
              <p className="text-xs text-slate-600 mt-1 text-right">{moderatorPrompt.length}/4000</p>
            </div>
          </div>
        </section>

        <div className="flex items-center gap-3">
          <button
            onClick={handleSave}
            disabled={saving || !dirty}
            className="px-6 py-2.5 bg-gradient-to-r from-indigo-600 to-cyan-600 hover:from-indigo-500 hover:to-cyan-500 text-white font-medium rounded-xl shadow-lg shadow-indigo-500/20 disabled:opacity-40 transition-all text-sm"
          >
            {saving ? "Kaydediliyor…" : "Kaydet"}
          </button>
          {!dirty && !success && (
            <span className="text-xs text-slate-600">Kaydedilmemiş değişiklik yok</span>
          )}
          {success && <span className="text-sm text-emerald-400 animate-fade-in">Kaydedildi</span>}
          {error && <span className="text-sm text-red-400 animate-fade-in">{error}</span>}
        </div>

        <div className="pb-8" />
      </main>
    </AppShell>
  );
}
